import { useEffect, useState } from "react";
import api from "@/lib/api";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import InputAdornment from "@mui/material/InputAdornment";
import { alpha } from "@mui/material/styles";
import { Plus, X, Star, ChevronLeft, ChevronRight, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import PhotoUploader from "./PhotoUploader";

const AMENITIES_FULL = [
  "Private Pool", "Ocean View", "Wi-Fi", "Air Conditioning", "Chef on Request", "Daily Housekeeping",
  "Concierge", "Parking", "Jacuzzi", "Home Theatre", "BBQ", "Gym", "Beach Access", "Garden",
];
const ACTIVITIES_FULL = ["Yoga Deck", "Sunset Cruise", "Wine Tasting", "Snorkeling", "Spa", "Hiking"];
const DEFAULT_RULES = [
  "No smoking indoors", "No loud parties after 10 PM",
  "Check-in 3 PM / Check-out 11 AM", "Pets only on approval",
];
const POLICIES = [
  { id: "flexible", label: "Flexible", text: "Free cancellation up to 3 days before check-in. Full refund after that, minus the service fee." },
  { id: "moderate", label: "Moderate", text: "Free cancellation up to 7 days before check-in. 50% refund up to 48 hours before check-in. No refund within 48 hours." },
  { id: "firm", label: "Firm", text: "Free cancellation up to 14 days before check-in. 50% refund up to 7 days before check-in. No refund within 7 days." },
  { id: "custom", label: "Custom", text: "" },
];

const STEPS = ["Basics", "Photos", "Details", "Pricing", "Review"];

function emptyForm() {
  return {
    name: "", location: "", destination_region: "",
    bedrooms: 3, bathrooms: 3, max_guests: 6,
    categories: ["new"],
    images: [],
    description: "",
    amenities: [],
    activities: [],
    house_rules: [...DEFAULT_RULES],
    pet_friendly: false,
    instant_book: true,
    price_per_night: "",
    policy_preset: "moderate",
    cancellation_policy: POLICIES.find((p) => p.id === "moderate").text,
    video_url: "",
    virtual_tour_url: "",
  };
}

function ChipPicker({ options, value, onChange, testIdPrefix }) {
  const toggle = (opt) => onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {options.map((opt) => (
        <Chip
          key={opt}
          label={opt}
          onClick={() => toggle(opt)}
          data-testid={`${testIdPrefix}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
          variant={value.includes(opt) ? "filled" : "outlined"}
          sx={value.includes(opt) ? { bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 600 } : { fontWeight: 500 }}
        />
      ))}
    </Box>
  );
}

function CustomAdder({ onAdd, placeholder, testId }) {
  const [v, setV] = useState("");
  const submit = () => {
    if (!v.trim()) return;
    onAdd(v.trim());
    setV("");
  };
  return (
    <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
      <TextField
        size="small" fullWidth placeholder={placeholder} value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
        inputProps={{ "data-testid": testId }}
      />
      <Button variant="outlined" onClick={submit} startIcon={<Plus size={14} />}>Add</Button>
    </Box>
  );
}

export default function VillaWizard({ villa, onSaved, onCancel }) {
  const editing = !!villa;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!villa) return;
    const preset = POLICIES.find((p) => p.text === villa.cancellation_policy);
    setForm({
      name: villa.name || "", location: villa.location || "", destination_region: villa.destination_region || "",
      bedrooms: villa.bedrooms || 1, bathrooms: villa.bathrooms || 1, max_guests: villa.max_guests || 2,
      categories: villa.categories || [],
      images: villa.images || [],
      description: villa.description || "",
      amenities: villa.amenities || [],
      activities: villa.activities || [],
      house_rules: villa.house_rules?.length ? villa.house_rules : [...DEFAULT_RULES],
      pet_friendly: !!villa.pet_friendly,
      instant_book: villa.instant_book !== false,
      price_per_night: villa.price_per_night || "",
      policy_preset: preset ? preset.id : "custom",
      cancellation_policy: villa.cancellation_policy || "",
      video_url: villa.video_url || "",
      virtual_tour_url: villa.virtual_tour_url || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [villa]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const validate = (s) => {
    if (s === 0) {
      if (!form.name.trim()) return "Give your villa a name";
      if (!form.location.trim()) return "Add the villa's location";
      if (!form.destination_region.trim()) return "Add a destination region";
      if (!form.bedrooms || !form.bathrooms || !form.max_guests) return "Bedrooms, bathrooms and max guests must be at least 1";
    }
    if (s === 1 && form.images.length === 0) return "Upload at least one photo";
    if (s === 2 && !form.description.trim()) return "Add a description";
    if (s === 3 && !form.price_per_night) return "Set a price per night";
    return null;
  };

  const next = () => {
    const err = validate(step);
    if (err) { toast(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    for (let s = 0; s < STEPS.length - 1; s++) {
      const err = validate(s);
      if (err) { toast(err); setStep(s); return; }
    }
    setSubmitting(true);
    const payload = {
      name: form.name, location: form.location, destination_region: form.destination_region,
      bedrooms: parseInt(form.bedrooms), bathrooms: parseInt(form.bathrooms), max_guests: parseInt(form.max_guests),
      categories: form.categories,
      images: form.images,
      description: form.description,
      amenities: form.amenities,
      activities: form.activities,
      house_rules: form.house_rules,
      cancellation_policy: form.cancellation_policy,
      pet_friendly: form.pet_friendly,
      instant_book: form.instant_book,
      price_per_night: parseFloat(form.price_per_night),
      video_url: form.video_url.trim() || null,
      virtual_tour_url: form.virtual_tour_url.trim() || null,
    };
    try {
      if (editing) await api.patch(`/owner/villas/${villa.id}`, payload);
      else await api.post("/owner/villas", payload);
      toast(editing ? "Villa updated" : "Villa listed");
      onSaved?.();
    } catch (err) {
      toast(err.response?.data?.detail || "Could not save villa");
    } finally {
      setSubmitting(false);
    }
  };

  const numberField = (key, label, min = 0) => (
    <TextField
      label={label} type="number" value={form[key]}
      onChange={(e) => set({ [key]: e.target.value })}
      inputProps={{ "data-testid": `villa-wizard-${key}`, min }}
      fullWidth
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 420 }}>
      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4, "& .MuiStepLabel-label": { fontSize: 12 } }}>
        {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>

      <Box sx={{ flex: 1 }}>
        {step === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField label="Villa name" required value={form.name} onChange={(e) => set({ name: e.target.value })} inputProps={{ "data-testid": "villa-wizard-name" }} fullWidth />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField label="Location (city, region)" required value={form.location} onChange={(e) => set({ location: e.target.value })} inputProps={{ "data-testid": "villa-wizard-location" }} fullWidth />
              <TextField label="Destination region" required value={form.destination_region} onChange={(e) => set({ destination_region: e.target.value })} inputProps={{ "data-testid": "villa-wizard-destination_region" }} fullWidth />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
              {numberField("bedrooms", "Bedrooms", 1)}
              {numberField("bathrooms", "Bathrooms", 1)}
              {numberField("max_guests", "Max guests", 1)}
            </Box>
          </Box>
        )}

        {step === 1 && (
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5 }}>Add photos</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              The first photo is your cover — it's what guests see first in search results.
            </Typography>
            <PhotoUploader value={form.images} onChange={(images) => set({ images })} testIdPrefix="villa-wizard-photo" />
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField label="Description" required multiline rows={4} value={form.description} onChange={(e) => set({ description: e.target.value })} inputProps={{ "data-testid": "villa-wizard-description" }} fullWidth />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>Amenities</Typography>
              <ChipPicker options={AMENITIES_FULL} value={form.amenities} onChange={(v) => set({ amenities: v })} testIdPrefix="villa-wizard-amenity" />
              <CustomAdder placeholder="Add a custom amenity…" testId="villa-wizard-amenity-custom" onAdd={(v) => !form.amenities.includes(v) && set({ amenities: [...form.amenities, v] })} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>Activities</Typography>
              <ChipPicker options={ACTIVITIES_FULL} value={form.activities} onChange={(v) => set({ activities: v })} testIdPrefix="villa-wizard-activity" />
              <CustomAdder placeholder="Add a custom activity…" testId="villa-wizard-activity-custom" onAdd={(v) => !form.activities.includes(v) && set({ activities: [...form.activities, v] })} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>House rules</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {form.house_rules.map((rule, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, borderRadius: 2.5, border: 1, borderColor: "divider", px: 1.5, py: 0.75 }} data-testid={`villa-wizard-rule-${i}`}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{rule}</Typography>
                    <IconButton size="small" onClick={() => set({ house_rules: form.house_rules.filter((_, idx) => idx !== i) })} data-testid={`villa-wizard-rule-remove-${i}`}>
                      <X size={13} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              <CustomAdder placeholder="Add a house rule…" testId="villa-wizard-rule-custom" onAdd={(v) => set({ house_rules: [...form.house_rules, v] })} />
            </Box>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <FormControlLabel control={<Switch checked={form.pet_friendly} onChange={(e) => set({ pet_friendly: e.target.checked })} inputProps={{ "data-testid": "villa-wizard-pet-friendly" }} />} label="Pet friendly" />
              <FormControlLabel control={<Switch checked={form.instant_book} onChange={(e) => set({ instant_book: e.target.checked })} inputProps={{ "data-testid": "villa-wizard-instant-book" }} />} label="Instant book" />
            </Box>
          </Box>
        )}

        {step === 3 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField
              label="Price per night" required type="number" value={form.price_per_night}
              onChange={(e) => set({ price_per_night: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              inputProps={{ "data-testid": "villa-wizard-price" }}
              fullWidth
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>Cancellation policy</Typography>
              <Select
                fullWidth value={form.policy_preset}
                onChange={(e) => {
                  const preset = POLICIES.find((p) => p.id === e.target.value);
                  set({ policy_preset: preset.id, cancellation_policy: preset.id === "custom" ? form.cancellation_policy : preset.text });
                }}
                inputProps={{ "data-testid": "villa-wizard-policy-preset" }}
              >
                {POLICIES.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
              </Select>
              <TextField
                sx={{ mt: 1.5 }} fullWidth multiline rows={2}
                value={form.cancellation_policy}
                disabled={form.policy_preset !== "custom"}
                onChange={(e) => set({ cancellation_policy: e.target.value })}
                inputProps={{ "data-testid": "villa-wizard-policy-text" }}
              />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField label="Video tour URL (optional)" value={form.video_url} onChange={(e) => set({ video_url: e.target.value })} inputProps={{ "data-testid": "villa-wizard-video-url" }} fullWidth />
              <TextField label="Virtual tour URL (optional)" value={form.virtual_tour_url} onChange={(e) => set({ virtual_tour_url: e.target.value })} inputProps={{ "data-testid": "villa-wizard-virtual-tour-url" }} fullWidth />
            </Box>
          </Box>
        )}

        {step === 4 && (
          <ReviewStep form={form} />
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={step === 0 ? onCancel : back} startIcon={step > 0 ? <ChevronLeft size={15} /> : undefined} data-testid="villa-wizard-back">
          {step === 0 ? "Cancel" : "Back"}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button variant="contained" onClick={next} endIcon={<ChevronRight size={15} />} data-testid="villa-wizard-next">Next</Button>
        ) : (
          <Button variant="contained" onClick={submit} disabled={submitting} data-testid="villa-wizard-submit">
            {submitting ? "Saving…" : editing ? "Save changes" : "List villa"}
          </Button>
        )}
      </Box>
    </Box>
  );
}

function ReviewStep({ form }) {
  const cover = form.images[0];
  return (
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 2 }}>Review your listing</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" }, gap: 2.5, borderRadius: 4, border: 1, borderColor: "divider", overflow: "hidden", p: 2 }}>
        <Box sx={{ aspectRatio: "4 / 3", borderRadius: 3, overflow: "hidden", bgcolor: (t) => alpha(t.palette.text.primary, 0.05), display: "flex", alignItems: "center", justifyContent: "center" }}>
          {cover ? <Box component="img" src={cover} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <HomeIcon size={28} className="text-gold" />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography className="font-serif-display" sx={{ fontSize: 22 }}>{form.name || "Untitled villa"}</Typography>
          <Typography variant="body2" color="text.secondary">{form.location || "—"}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {form.bedrooms} bed · {form.bathrooms} bath · up to {form.max_guests} guests
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: 18, mt: 1 }}>
            ₹{form.price_per_night ? Number(form.price_per_night).toLocaleString() : "—"} <Typography component="span" variant="body2" color="text.secondary">/ night</Typography>
          </Typography>
          {form.categories.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.5 }}>
              {form.categories.map((c) => <Chip key={c} label={c} size="small" sx={{ textTransform: "capitalize" }} />)}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {form.description || "No description yet."}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 2.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>Photos</Typography>
          <Typography variant="body2">{form.images.length} uploaded</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>Amenities</Typography>
          <Typography variant="body2">{form.amenities.length ? form.amenities.join(", ") : "None selected"}</Typography>
        </Box>
      </Box>
    </Box>
  );
}
